/**
 * How fast Bilal's recorded reply is played back.
 *
 * The provider's own `speed` is already at its ceiling: 1.2 is accepted and 1.3 is refused with a
 * validation error. Anything faster has to come from playback, which the browser does for free —
 * no second synthesis, no re-encoding, no added wait, and `preservesPitch` keeps the voice from
 * turning into a chipmunk.
 *
 * It is a person's own preference and it is kept on their own device. Nothing about it reaches the
 * server, so it is not a stored data class and carries no retention question.
 */
export const SPEECH_RATES = Object.freeze([1, 1.15, 1.3, 1.5] as const);
export type SpeechRate = (typeof SPEECH_RATES)[number];

export const DEFAULT_SPEECH_RATE: SpeechRate = 1.15;

const KEY = 'bilal.speechRate';

/**
 * Reading it must never be the reason speech fails. A private window, cleared site data or a
 * browser configured to refuse storage all throw here, and every one of them means "no preference
 * saved" rather than "do not play".
 */
export function readSpeechRate(storage?: Pick<Storage, 'getItem'>): SpeechRate {
  try {
    const store = storage ?? globalThis.localStorage;
    const saved = Number(store.getItem(KEY));
    return SPEECH_RATES.find((rate) => rate === saved) ?? DEFAULT_SPEECH_RATE;
  } catch {
    return DEFAULT_SPEECH_RATE;
  }
}

export function writeSpeechRate(rate: SpeechRate, storage?: Pick<Storage, 'setItem'>): void {
  try {
    (storage ?? globalThis.localStorage).setItem(KEY, String(rate));
  } catch {
    /* a preference that cannot be saved is still a preference for this session */
  }
}

/** The next rate in the cycle, so one control covers all of them. */
export function nextSpeechRate(rate: SpeechRate): SpeechRate {
  const at = SPEECH_RATES.indexOf(rate);
  return SPEECH_RATES[(at + 1) % SPEECH_RATES.length] ?? DEFAULT_SPEECH_RATE;
}
